import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { Award, TrendingUp, MapPin } from "lucide-react";
import { getPromoConfig } from "@/lib/promo";
import { useSeo } from "@/lib/seo";

export default function SoldShowcase() {
    useSeo({ title: "Case vendute e affittate: i risultati di VendiDirect", description: "Scopri gli immobili venduti e affittati su VendiDirect: prezzi, tempi e città. La prova concreta che vendere casa in modo diretto funziona.", canonical: "/venduti" });
    const [data, setData] = useState(null);
    const [promoCopy, setPromoCopy] = useState("Unisciti ai proprietari che hanno scelto la via diretta.");

    useEffect(() => {
        api.get("/social-proof/sold?limit=24").then((r) => setData(r.data));
        getPromoConfig().then((cfg) => {
            if (cfg?.any_promo) {
                setPromoCopy(`Unisciti ai proprietari che hanno scelto la via diretta. Pubblica gratis per ${cfg.max_months} mes${cfg.max_months === 1 ? "e" : "i"}.`);
            }
        });
    }, []);

    if (!data) return <div className="pt-32 text-center text-ink-400">Caricamento...</div>;
    const c = data.counters || {};

    return (
        <div data-testid="sold-showcase" className="pt-32 pb-24">
            <div className="w-full px-6 md:px-12 lg:px-20 2xl:px-28">
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <span className="label-eyebrow">La prova</span>
                    <h1 className="font-serif text-5xl sm:text-6xl text-ink-50 mt-4 leading-tight">
                        Case che hanno cambiato <span className="italic text-gold">vita</span>
                    </h1>
                    <p className="text-ink-400 mt-6 leading-relaxed">
                        Una selezione di immobili venduti e affittati direttamente sulla nostra piattaforma: proprietari, agenzie e clienti che si sono trovati.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
                    <div data-testid="counter-7d" className="border border-gold/30 bg-ink-900/40 p-8 text-center">
                        <Award size={28} className="text-gold mx-auto mb-3" />
                        <div className="font-serif text-5xl text-gold">{c.last_7_days || 0}</div>
                        <div className="text-xs text-ink-400 mt-2 tracking-[0.2em] uppercase">case · ultimi 7 giorni</div>
                    </div>
                    <div data-testid="counter-30d" className="border border-gold/30 bg-ink-900/40 p-8 text-center">
                        <TrendingUp size={28} className="text-gold mx-auto mb-3" />
                        <div className="font-serif text-5xl text-gold">{c.last_30_days || 0}</div>
                        <div className="text-xs text-ink-400 mt-2 tracking-[0.2em] uppercase">case · ultimi 30 giorni</div>
                    </div>
                    <div data-testid="counter-total" className="border border-gold/30 bg-ink-900/40 p-8 text-center">
                        <div className="font-serif text-5xl text-gold">{c.total || 0}</div>
                        <div className="text-xs text-ink-400 mt-2 tracking-[0.2em] uppercase">totali su vendidirect.it</div>
                    </div>
                </div>

                {(data.recent || []).length === 0 ? (
                    <div className="text-center py-20 border border-white/10 bg-ink-900/30">
                        <p className="font-serif text-2xl text-ink-50 mb-3">Stiamo costruendo la storia</p>
                        <p className="text-ink-400 text-sm max-w-md mx-auto">Le prime case vendute appariranno qui presto. Vuoi essere la prima?</p>
                        <Link to="/pubblica" className="inline-block mt-6 cta-gold px-6 py-3 text-sm">Pubblica il tuo annuncio</Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {data.recent.map((p, i) => {
                            const isRent = p.status === "rented";
                            return (
                                <div key={i} data-testid={`sold-item-${i}`} className="group relative overflow-hidden">
                                    <div className="aspect-[4/5] bg-ink-900 overflow-hidden">
                                        <img src={(p.photos || [])[0] || "https://images.unsplash.com/photo-1545597390-c92ae038a5a8?auto=format&fit=crop&w=1200&q=80"} alt="" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 grayscale-[0.4] group-hover:grayscale-0" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
                                        <div className="absolute top-4 right-4">
                                            <span className="bg-gold text-black px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-bold">
                                                {isRent ? "✓ Affittato" : "✓ Venduto"}
                                            </span>
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 p-5">
                                            <h3 className="font-serif text-lg text-ink-50 line-clamp-2">{p.title}</h3>
                                            <div className="flex items-center gap-1 text-xs text-ink-400 mt-2">
                                                <MapPin size={11} /> {p.city}, {p.region}
                                            </div>
                                            <div className="text-price font-serif text-xl mt-3">{formatPrice(p.price)}{isRent && <span className="text-xs text-ink-400 font-sans ml-1">/mese</span>}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="mt-24 text-center max-w-2xl mx-auto p-12 border border-gold/30 bg-gradient-to-br from-gold/10 via-transparent to-transparent">
                    <h2 className="font-serif text-3xl text-ink-50">Anche la tua casa, qui</h2>
                    <p className="text-ink-400 text-sm mt-4">{promoCopy}</p>
                    <Link to="/pubblica" className="inline-block mt-6 cta-gold cta-gold-pulse px-8 py-4 text-sm">Pubblica il tuo annuncio →</Link>
                </div>
            </div>
        </div>
    );
}
